import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';
import type { WhatMembersNeedTopNeedRow } from '@act/shared/data-transfer-objects';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import {
  FONT_BODY,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from './BrandChart';

/** Studio SVG fills for the top-5 ranked-need bars (solid steps, no opacity). */
const WHAT_MEMBERS_NEED_BAR_COLORS = [
  'var(--reema-coral-100)',
  'var(--reema-midnight-100)',
  '#0A3356',
  '#154A74',
  '#1F6192',
] as const;

const SCREEN_CHART_HEIGHT = 240;
const PRINT_CHART_HEIGHT = 240;
const LAST_BAR_COLOR =
  WHAT_MEMBERS_NEED_BAR_COLORS[WHAT_MEMBERS_NEED_BAR_COLORS.length - 1];

const chartLayout = (printStatic: boolean) =>
  printStatic
    ? {
        margin: { top: 8, right: 28, bottom: 8, left: 0 },
        barSize: 20,
        barCategoryGap: 16,
        tickFontSize: 11,
        labelFontSize: 11,
        yAxisWidth: 100,
      }
    : {
        margin: { top: 12, right: 28, bottom: 4, left: 0 },
        barSize: 26,
        barCategoryGap: 16,
        tickFontSize: 11,
        labelFontSize: 11,
        yAxisWidth: 100,
      };

interface TopNeedChartRow {
  label: string;
  pct: number;
  fill: string;
}

const formatPctTick = (value: number): string => `${Math.round(value)}%`;

const toChartRows = (rows: WhatMembersNeedTopNeedRow[]): TopNeedChartRow[] => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return rows.map((row, index) => ({
    label: row.label,
    pct:
      row.pct ??
      (total > 0 ? Math.round((row.count / total) * 100) : 0),
    fill: WHAT_MEMBERS_NEED_BAR_COLORS[index] ?? LAST_BAR_COLOR,
  }));
};

const xAxisMax = (maxPct: number): number =>
  Math.max(Math.ceil(maxPct / 4) * 4, 4);

/**
 * Horizontal ranked-need bar chart for What members need most.
 * @param props.rows - Ranked need rows (already capped at top 5).
 * @param props.printStatic - When true, use print-safe sizing and skip animation.
 */
export const WhatMembersNeedTopNeedsChart = ({
  rows,
  printStatic,
}: {
  rows: WhatMembersNeedTopNeedRow[];
  printStatic: boolean;
}) => {
  const chartHeight = printStatic ? PRINT_CHART_HEIGHT : SCREEN_CHART_HEIGHT;
  const layout = chartLayout(printStatic);
  const { containerRef, dimensions } = useReportChartDimensions(chartHeight);
  const chartRows = toChartRows(rows);
  const maxPct = Math.max(...chartRows.map(row => row.pct), 0);
  const xMax = xAxisMax(maxPct);
  const tickStep = xMax / 4;
  const ticks = [0, tickStep, tickStep * 2, tickStep * 3, xMax];

  return (
    <div
      ref={containerRef}
      className="what-members-need-top-needs-chart report-chart-host"
      style={{ width: '100%', minWidth: 0, height: chartHeight }}
    >
      {dimensions ? (
        <BarChart
          width={dimensions.width}
          height={dimensions.height}
          data={chartRows}
          layout="vertical"
          margin={layout.margin}
          barCategoryGap={layout.barCategoryGap}
        >
          <XAxis
            type="number"
            domain={[0, xMax]}
            ticks={ticks}
            tickFormatter={formatPctTick}
            axisLine={false}
            tickLine={false}
            tick={{
              fontFamily: FONT_BODY,
              fontSize: layout.tickFontSize,
              fontWeight: 400,
              fill: TEXT_SECONDARY,
            }}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={layout.yAxisWidth}
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={{
              fontFamily: FONT_BODY,
              fontSize: layout.tickFontSize,
              fontWeight: 500,
              fill: TEXT_PRIMARY,
            }}
          />
          <Bar
            dataKey="pct"
            radius={[0, 4, 4, 0]}
            barSize={layout.barSize}
            isAnimationActive={!printStatic}
          >
            {chartRows.map(row => (
              <Cell key={row.label} fill={row.fill} />
            ))}
            <LabelList
              dataKey="pct"
              position="right"
              formatter={formatPctTick}
              style={{
                fontFamily: FONT_BODY,
                fontWeight: 500,
                fontSize: layout.labelFontSize,
                fill: TEXT_SECONDARY,
              }}
            />
          </Bar>
        </BarChart>
      ) : null}
    </div>
  );
};
